import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { PrinciplesList } from '../components/ui/PrinciplesList';
import { ProcessSteps } from '../components/ui/ProcessSteps';
import { ButtonLink } from '../components/ui/ButtonLink';
import { EmployerInquiryPanel } from '../components/sections/EmployerInquiryPanel';
import { employerBenefits, employerExpectations } from '../data/audiences';
const nextSteps = ['We review your inquiry and come back to you to arrange a conversation.', 'We discuss the role, organization and timing in confidence.', 'We propose a search approach, scope and timeline for your consideration.'];
export function Employers() {
  usePageMeta('For Employers | Avenqor Talent Search', 'Structured executive search, targeted sourcing, professional engagement and candidate evaluation for organizations hiring senior and specialist talent.');
  return <>
      <PageHero eyebrow="For Employers" title="Find the People Who Move Your Organization Forward." intro="Senior and specialist hires shape strategy, culture and performance for years. We help you approach them with structure, insight and discretion." data-id="element-466" data-mp="3elye7q5tmo5l">
        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-467" data-mp="3aiiicw1ksrty">
          <ButtonLink to="#employer-inquiry" inPage variant="gold" data-id="element-468" data-mp="2vv77yxh4rdvf">
            Discuss Your Hiring Needs
          </ButtonLink>
          <ButtonLink to="/services" variant="outlineLight" data-id="element-469" data-mp="30mp9bmn2fggx">
            Explore Our Services
          </ButtonLink>
        </div>
      </PageHero>

      <section className="bg-white" aria-labelledby="value-title" data-id="element-470" data-mp="2msftspwve403">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-id="element-471" data-mp="2qwdu33xxlkuq">
          <SectionHeading id="value-title" eyebrow="The Value of Structured Search" title="A wider field, assessed more carefully." intro="The strongest candidates are often not looking. A structured search reaches them professionally, and evaluates them against what the role genuinely requires." data-id="element-472" data-mp="2cvtc29zubt2a" />
          <div className="mt-16" data-id="element-473" data-mp="imecdd90ua3n">
            <PrinciplesList items={employerBenefits} columns={4} data-id="element-474" data-mp="eluhkgbx7iko" />
          </div>
        </div>
      </section>

      <section className="bg-paper" aria-labelledby="employer-process-title" data-id="element-475" data-mp="9oly8cf1d8hu">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-id="element-476" data-mp="16ebqjazy1e7x">
          <SectionHeading id="employer-process-title" eyebrow="The Search Process" title="What working with us looks like." data-id="element-477" data-mp="16bh4jf1a7lce" />
          <div className="mt-16" data-id="element-478" data-mp="xclxy1ubs98k">
            <ProcessSteps showDetail data-id="element-479" data-mp="xbu5v0zxstn1" />
          </div>
        </div>
      </section>

      <section className="bg-white" aria-labelledby="expectations-title" data-id="element-480" data-mp="sczakzks9cm1">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-id="element-481" data-mp="3i32uclrsjs8j">
          <SectionHeading id="expectations-title" title="A partnership with clear expectations." data-id="element-482" data-mp="3i0zy3k01p2vd" />
          <div className="mt-14 grid gap-12 md:grid-cols-2 md:gap-16" data-id="element-483" data-mp="3d6ma42nppy0q">
            {[{
            title: 'What you can expect from us',
            items: employerExpectations.fromUs
          }, {
            title: 'What helps a search succeed',
            items: employerExpectations.fromYou
          }].map(group => <div key={group.title} data-id="element-484" data-mp="3cwo3etyf54o4">
                <h3 className="font-serif text-[1.65rem] font-medium text-navy" data-id="element-485" data-mp="3hph8xk1poh19">{group.title}</h3>
                <ul className="mt-6 border-b border-line" data-id="element-486" data-mp="3lrlzub0ryzk7">
                  {group.items.map(item => <li key={item} className="flex gap-4 border-t border-line py-4 text-[15px] leading-relaxed text-ink" data-id="element-487" data-mp="3qovrlxbmznb7">
                      <span aria-hidden="true" className="mt-[11px] h-px w-4 shrink-0 bg-gold" data-id="element-488" data-mp="3vprwoe7q5fi7" />
                      {item}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-paper" aria-labelledby="inquiry-title" data-id="element-489" data-mp="26b3lggave3lr">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:py-32" data-id="element-490" data-mp="ljg9bg9t5ex">
          <div className="lg:col-span-4" data-id="element-491" data-mp="3rsm6o0oab7o3">
            <SectionHeading id="inquiry-title" eyebrow="Start a Conversation" title="Tell us about the role." intro="A short inquiry is enough to begin. There is no obligation, and details are handled in confidence." data-id="element-492" data-mp="3rrkt9pjsq937" />
            <h3 className="mt-12 text-sm font-semibold text-navy" data-id="element-493" data-mp="3d3zv8a1s3025">What happens next</h3>
            <ol className="mt-5 space-y-5" data-id="element-494" data-mp="392mkpmnqq8ow">
              {nextSteps.map((step, index) => <li key={step} className="flex gap-4 text-[15px] leading-relaxed text-ink" data-id="element-495" data-mp="3mu1i4yieacfh" data-mpi={index}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gold font-serif text-sm font-semibold text-navy" data-id="element-496" data-mp="3i21a0is68d7t">
                    {index + 1}
                  </span>
                  {step}
                </li>)}
            </ol>
          </div>
          <div className="lg:col-span-8" data-id="element-497" data-mp="14uiflwm7oxqw">
            <EmployerInquiryPanel data-id="element-498" data-mp="14tql6p4cp627" />
          </div>
        </div>
      </section>
    </>;
}